import React from 'react';

import {Artifact, FetchJobVersionsQuery} from '../../generated/graphql';
import {Select} from '../Form/Select';
import {Alias} from './Alias';
import {
  convertArtifactToVersion,
  findVersionNumber,
} from './JobDetail/utilData';
import {JobVersions} from './JobDetail/utilTypes';
import * as S from './SelectJobVersion.styles';

// Option for an alias/version
type JobVersionOption = {
  label: string;
  value: string;
  // This should be set for everything other than "All".
  version?: number;
};

// Option for group separator in select
export type JobVersionGroupOption = {
  label: string;
  options: JobVersionOption[];
};

export type JobVersionOptions = Array<JobVersionOption | JobVersionGroupOption>;

type SelectJobVersionProps = {
  jobVersions: JobVersions;
  includeAll?: boolean;
  selectedAlias: string;
  onSelectJobAlias?: (jobAlias: string, version?: number) => void;
};

const RE_VERSION = /^v(\d+)$/;

// Get the jobversions and the default alias to select.
export const getJobVersionsFromQuery = (
  data: FetchJobVersionsQuery
): [JobVersions, string] => {
  const edges = data.project?.artifactCollection?.artifacts.edges ?? [];
  const jobVersions = edges.map(edge =>
    convertArtifactToVersion(edge.node as Artifact)
  );
  // Look for latest or biggest version number
  let hasLatest = false;
  const versionNumbers = [];
  for (const jobVersion of jobVersions) {
    if (jobVersion.aliases.some(a => a.alias === 'latest')) {
      hasLatest = true;
      break;
    }
    versionNumbers.push(findVersionNumber(jobVersion.aliases));
  }
  versionNumbers.sort((a, b) => b - a);
  const defaultAlias = hasLatest
    ? 'latest'
    : versionNumbers.length
    ? `v${versionNumbers[0]}`
    : '';
  return [jobVersions, defaultAlias];
};

// Convert JobVersions into nested select widget options
// and a mapping from alias string to option.
const getOptionsFromJobVersions = (
  jobVersions: JobVersions,
  includeAll: boolean
) => {
  const optionMap: Record<string, JobVersionOption> = {};

  // This will be used to display the version number next to each alias.
  const mapAliasToVersionNumber: Record<string, number> = {};

  // Separate out version and non-version aliases.
  const aliases: string[] = [];
  const versions: number[] = [];
  for (const version of jobVersions) {
    const versionNumber = findVersionNumber(version.aliases);
    for (const artifactAlias of version.aliases) {
      const alias = artifactAlias.alias;
      mapAliasToVersionNumber[alias] = versionNumber;
      const match = alias.match(RE_VERSION);
      if (match) {
        versions.push(parseInt(match[1], 10));
      } else {
        aliases.push(alias);
      }
    }
  }

  // "latest" is special, sort it first.
  const sortedAliases = aliases.sort((a, b) => {
    if (a === 'latest') {
      return b === 'latest' ? 0 : -1;
    }
    if (b === 'latest') {
      return 1;
    }
    return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
  });
  const aliasOptions = sortedAliases.map(a => ({
    value: a,
    label: a,
    version: mapAliasToVersionNumber[a],
  }));
  aliasOptions.forEach(opt => (optionMap[opt.value] = opt));

  // Sort version numbers in descending order
  const sortedVersions = versions.sort((a, b) => b - a);
  const versionOptions = sortedVersions.map(v => ({
    value: `v${v}`,
    label: `v${v}`,
    version: v,
  }));
  versionOptions.forEach(opt => (optionMap[opt.value] = opt));

  const options: JobVersionOptions = [
    {
      label: 'Aliases',
      options: aliasOptions,
    },
    {
      label: 'Versions',
      options: versionOptions,
    },
  ];
  if (includeAll) {
    const allOption = {
      value: '*',
      label: 'All versions',
    };
    options.unshift(allOption);
    optionMap['*'] = allOption;
  }
  return {options, optionMap};
};

// Parse query results to separate aliases from versions.
export const getJobVersionOptions = (
  data: FetchJobVersionsQuery
): [JobVersionGroupOption[], string] => {
  const aliases: string[] = [];
  const versions: number[] = [];

  const artifactEdges = data.project?.artifactCollection?.artifacts.edges ?? [];
  for (const artifactEdge of artifactEdges) {
    const artifactAliases = artifactEdge.node.aliases;
    for (const artifactAlias of artifactAliases) {
      const match = artifactAlias.alias.match(RE_VERSION);
      if (match) {
        versions.push(parseInt(match[1], 10));
      } else {
        aliases.push(artifactAlias.alias);
      }
    }
  }

  // "latest" is special, sort it first.
  const sortedAliases = aliases.sort((a, b) => {
    if (a === 'latest') {
      return b === 'latest' ? 0 : -1;
    }
    if (b === 'latest') {
      return 1;
    }
    return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
  });
  const sortedVersions = versions.sort().reverse();
  const aliasOptions = sortedAliases.map(a => ({value: a, label: a}));
  const versionOptions = sortedVersions.map(v => ({
    value: `v${v}`,
    label: `v${v}`,
  }));
  const options = [
    {
      label: 'Aliases',
      options: aliasOptions,
    },
    {
      label: 'Versions',
      options: versionOptions,
    },
  ];

  // At the moment, users are permitted to delete "latest".
  // If they have, fall back to highest version number.
  const defaultVersion =
    aliases.length && aliases[0] === 'latest' ? 'latest' : `v${versions[0]}`;

  return [options, defaultVersion];
};

const formatOptionLabel = ({value, label, version}: JobVersionOption) => {
  if (value === '*') {
    return 'All versions';
  }
  if (value.match(RE_VERSION)) {
    return label;
  }
  return (
    <S.JobAliasOption>
      <S.Alias>
        <Alias
          alias={{
            alias: value,
          }}
        />
      </S.Alias>
      <S.AliasVersion>v{version}</S.AliasVersion>
    </S.JobAliasOption>
  );
};

export const SelectJobVersion = ({
  jobVersions,
  selectedAlias,
  onSelectJobAlias,
  includeAll,
}: SelectJobVersionProps) => {
  const {options, optionMap} = getOptionsFromJobVersions(
    jobVersions,
    includeAll ?? false
  );

  const onChange = (option: JobVersionOption | null) => {
    if (option) {
      if (onSelectJobAlias) {
        onSelectJobAlias(option.value, option.version);
      }
    }
  };

  const value = optionMap[selectedAlias];
  return (
    <Select
      options={options}
      value={value}
      onChange={onChange}
      formatOptionLabel={formatOptionLabel}
    />
  );
};
